import { AnalyticsBrandEcommerceDimensions, AnalyticsComponentEvent, AnalyticsComponentProduct } from 'idt-analytics-api/models';
import { CategoriesHelpers } from 'idt-ecommerce-api/catalog/categories/categories.helpers';
import { EcommerceContext } from 'idt-ecommerce-api/context/context.models';
import { AnalyticsHelpersOysho } from '../helpers/brand-analytics.helpers.oy';
import { AnalyticsBrandEcommerceDimensionsOysho } from '../brandDimensions.oy';
import { AnalyticsEcommerceOyshoProduct, AnalyticsOyshoEcommerce } from '../models/brand-analytics-ecommerce.model';

export class AnalyticsEcommerceHelpersOysho {

  public static dimensionHelpers: AnalyticsBrandEcommerceDimensions = AnalyticsBrandEcommerceDimensionsOysho;

  public static getEcommerceInfo = (ecommerce: string, componentData: AnalyticsComponentEvent, context?: EcommerceContext) => {
    const info: Record<string, AnalyticsOyshoEcommerce> = {
      'checkout': AnalyticsEcommerceHelpersOysho.getEcommerceCheckout(componentData, context),
      'add': AnalyticsEcommerceHelpersOysho.getEcommerceAdd(componentData, context),
      'click': AnalyticsEcommerceHelpersOysho.getEcommerceClick(componentData, context),
      'detail': AnalyticsEcommerceHelpersOysho.getEcommerceDetail(componentData, context),
      'impressions': AnalyticsEcommerceHelpersOysho.getEcommerceImpressions(componentData, context),
      'promoClick': AnalyticsEcommerceHelpersOysho.getEcommercePromoClick(componentData, context),
      'promoView': AnalyticsEcommerceHelpersOysho.getEcommercePromoView(componentData, context),
      'purchase': AnalyticsEcommerceHelpersOysho.getEcommercePurchase(componentData, context),
      'remove': AnalyticsEcommerceHelpersOysho.getEcommerceRemove(componentData, context)
    };

    return info[ecommerce];
  };

  public static getDimensions(componentData: AnalyticsComponentEvent, dimensions: number[], context?: EcommerceContext) {
    const dimensionsObject: Record<string, string | undefined> = {};
    dimensions.forEach((dimension) => {
      dimensionsObject[`dimension${dimension}`] = this.dimensionHelpers[`${dimension}`](componentData, context);
    });
    return dimensionsObject;
  }

  public static getMetrics(componentData: AnalyticsComponentEvent, metrics: number[], context?: EcommerceContext) {
    const metricsObject: Record<string, string | undefined> = {};
    metrics.forEach((metric) => {
      metricsObject[`metric${metric}`] = this.dimensionHelpers[`getMetric${metric}`](componentData, context);
    });
    return metricsObject;
  }

  public static getAnalyticProducts(
    componentData: AnalyticsComponentEvent,
    context?: EcommerceContext
  ): AnalyticsEcommerceOyshoProduct[] {
    const ecommerceProducts: AnalyticsEcommerceOyshoProduct[] = [];
    componentData.products?.forEach((product, index) => {
      const componentDataWithIndex = { ...componentData, productIndex: index };
      const gridElementType = this.dimensionHelpers[134](componentDataWithIndex, context);
      const ecommerceProduct: AnalyticsEcommerceOyshoProduct = {
        name: product.nameEn || product.name,
        id: product.fullReference,
        price: product.priceInfo?.priceWithCurrencySymbol
          ? this.formatEcommerceProductPrice(product.priceInfo.priceWithCurrencySymbol.toString()) : undefined,
        category: this.getEcommerceProductCategory(componentDataWithIndex, context),
        // TODO: Diferenciar entre oyshosport y oysho
        brand: 'oysho',
        key: product.id,
        dimension134: gridElementType,
        dimension190: gridElementType === 'estilismo' ? this.dimensionHelpers[190](componentDataWithIndex, context) : 'no',
        ...this.getDimensions(componentDataWithIndex, [29, 96, 107, 109, 111, 113, 114, 115, 116, 121, 123, 144], context)
      };
      ecommerceProducts.push(ecommerceProduct);
    });
    return ecommerceProducts;
  }

  public static getEcommerceAdd(
    componentData: AnalyticsComponentEvent,
    context?: EcommerceContext
  ): AnalyticsOyshoEcommerce {
    if (componentData?.products?.length) {
      componentData = { ...componentData, products: this.formatProducts(componentData.products) };
    }
    const addEcommerce: AnalyticsOyshoEcommerce = {
      currencyCode: context?.store?.details?.currencyInfo?.currencyCode,
      add: {
        actionField: {
          list: componentData?.location === 'parrilla'
            ? `${componentData?.location}_${this.getCategoryPath(componentData?.categoryId, context)}` : componentData?.location
        },
        products: this.getAnalyticProducts(componentData, context).map((product, index) => {
          return {
            ...product,
            position: this.dimensionHelpers.getMetric5({ ...componentData, productIndex: index }, context),
            quantity: componentData?.products?.length ? componentData.products[index].quantity?.toString() : undefined,
            ...this.getDimensions({ ...componentData, productIndex: index }, [69, 91, 97, 118, 119, 120], context),
            ...this.getMetrics({ ...componentData, productIndex: index }, [4, 5], context)
          };
        })
      }
    };
    return addEcommerce;
  }

  public static getEcommerceCheckout(
    componentData: AnalyticsComponentEvent,
    context?: EcommerceContext
  ): AnalyticsOyshoEcommerce {
    if (componentData?.products?.length) {
      componentData = { ...componentData, products: this.formatProducts(componentData.products) };
    }
    const checkoutEcommerce: AnalyticsOyshoEcommerce = {
      currencyCode: context?.store?.details?.currencyInfo?.currencyCode,
      checkout: {
        actionField: {
          // TODO: Rellenar con paso del checkout (CESTA: 1, ENVIO: 2, PAGO:3)
          step: undefined
        },
        products: this.getAnalyticProducts(componentData, context).map((product, index) => {
          return {
            ...product,
            quantity: componentData?.products?.length ? componentData.products[index].quantity?.toString() : undefined,
            ...this.getDimensions({ ...componentData, productIndex: index }, [69, 91, 97, 118, 119, 120], context)
          };
        })
      }
    };
    return checkoutEcommerce;
  }

  public static getEcommerceClick(
    componentData: AnalyticsComponentEvent,
    context?: EcommerceContext
  ): AnalyticsOyshoEcommerce {
    if (componentData?.products?.length) {
      componentData = { ...componentData, products: this.formatProducts(componentData.products) };
    }
    const clickEcommerce: AnalyticsOyshoEcommerce = {
      currencyCode: context?.store?.details?.currencyInfo?.currencyCode,
      click: {
        actionField: {
          list: componentData?.location === 'parrilla'
            ? `${componentData?.location}_${this.getCategoryPath(componentData?.categoryId, context)}` : componentData?.location
        },
        products: this.getAnalyticProducts(componentData, context).map((product, index) => {
          return {
            ...product,
            position: this.dimensionHelpers.getMetric5({ ...componentData, productIndex: index }, context),
            ...this.getMetrics({ ...componentData, productIndex: index }, [5], context)
          };
        })
      }
    };
    return clickEcommerce;
  }

  public static getEcommerceDetail(
    componentData: AnalyticsComponentEvent,
    context?: EcommerceContext
  ): AnalyticsOyshoEcommerce {
    if (componentData?.products?.length) {
      componentData = { ...componentData, products: this.formatProducts(componentData.products) };
    }
    const detailEcommerce: AnalyticsOyshoEcommerce = {
      currencyCode: context?.store?.details?.currencyInfo?.currencyCode,
      detail: {
        actionField: {
          list: componentData?.location === 'parrilla'
            ? `${componentData?.location}_${this.getCategoryPath(componentData?.categoryId, context)}` : componentData?.location
        },
        products: this.getAnalyticProducts(componentData, context).map((product, index) => {
          return {
            ...product,
            position: this.dimensionHelpers.getMetric5({ ...componentData, productIndex: index }, context),
            ...this.getDimensions(componentData, [69, 118], context),
            ...this.getMetrics({ ...componentData, productIndex: index }, [5], context)
          };
        })
      }
    };
    return detailEcommerce;
  }

  public static getEcommerceImpressions(
    componentData: AnalyticsComponentEvent,
    context?: EcommerceContext
  ): AnalyticsOyshoEcommerce {
    if (componentData?.products?.length) {
      componentData = { ...componentData, products: this.formatProducts(componentData.products) };
    }
    const impressionsEcommerce: AnalyticsOyshoEcommerce = {
      currencyCode: context?.store?.details?.currencyInfo?.currencyCode,
      impressions: this.getAnalyticProducts(componentData, context).map((product, index) => {
        return {
          ...product,
          list: componentData?.location === 'parrilla'
            ? `${componentData?.location}_${this.getCategoryPath(componentData?.categoryId, context)}` : componentData?.location,
          position: this.dimensionHelpers.getMetric5({ ...componentData, productIndex: index }, context),
          ...this.getDimensions(componentData, [118], context),
          ...this.getMetrics({ ...componentData, productIndex: index }, [5], context)
        };
      })
    };
    return impressionsEcommerce;
  }

  // PromoClick y PromoView actualmente no se utilizan. Se deja declarada la estructura por si es necesario implementarlos en un futuro.
  public static getEcommercePromoClick(
    componentData: AnalyticsComponentEvent,
    context?: EcommerceContext
  ): AnalyticsOyshoEcommerce {
    const promoClickEcommerce: AnalyticsOyshoEcommerce = {
      currencyCode: context?.store?.details?.currencyInfo?.currencyCode,
      promoClick: {
        promotions: [{
          id: undefined,
          name: undefined,
          creative: undefined,
          position: undefined
        }]
      }
    };
    return promoClickEcommerce;
  }

  public static getEcommercePromoView(
    componentData: AnalyticsComponentEvent,
    context?: EcommerceContext
  ): AnalyticsOyshoEcommerce {
    const promoViewEcommerce: AnalyticsOyshoEcommerce = {
      currencyCode: context?.store?.details?.currencyInfo?.currencyCode,
      promoView: {
        promotions: [{
          id: undefined,
          name: undefined,
          creative: undefined,
          position: undefined
        }]
      }
    };
    return promoViewEcommerce;
  }

  public static getEcommercePurchase(
    componentData: AnalyticsComponentEvent,
    context?: EcommerceContext
  ): AnalyticsOyshoEcommerce {
    if (componentData?.products?.length) {
      componentData = { ...componentData, products: this.formatProducts(componentData.products) };
    }
    const purchaseEcommerce: AnalyticsOyshoEcommerce = {
      currencyCode: context?.store?.details?.currencyInfo?.currencyCode,
      purchase: {
        actionField: {
          // TODO: Rellenar con datos del pedido cuando se implemente la cesta
          id: undefined,
          revenue: undefined,
          shipping: undefined,
          // shippingDiscount - descuento en valor absoluto si lo hay (adjustment res checkout)
          tax: undefined
        },
        products: this.getAnalyticProducts(componentData, context).map((product, index) => {
          return {
            ...product,
            quantity: componentData?.products?.length ? componentData.products[index].quantity?.toString() : undefined,
            ...this.getDimensions({ ...componentData, productIndex: index }, [69, 91, 97, 118, 119, 120], context)
          };
        })
      }
    };
    return purchaseEcommerce;
  }

  public static getEcommerceRemove(
    componentData: AnalyticsComponentEvent,
    context?: EcommerceContext
  ): AnalyticsOyshoEcommerce {
    if (componentData?.products?.length) {
      componentData = { ...componentData, products: this.formatProducts(componentData.products) };
    }
    const purchaseEcommerce: AnalyticsOyshoEcommerce = {
      currencyCode: context?.store?.details?.currencyInfo?.currencyCode,
      remove: {
        products: this.getAnalyticProducts(componentData, context).map((product, index) => {
          return {
            ...product,
            quantity: componentData?.products?.length ? componentData.products[index].quantity?.toString() : undefined,
            ...this.getDimensions({ ...componentData, productIndex: index }, [69, 91, 97, 118, 119, 120], context),
            ...this.getMetrics({ ...componentData, productIndex: index }, [4], context)
          };
        })
      }
    };
    return purchaseEcommerce;
  }

  public static parseUrl(url?: string): string | undefined {
    return url?.split('oysho.com')[1] || url;
  }

  public static getEcommerceProductCategory(componentData: AnalyticsComponentEvent, context?: EcommerceContext): string {
    const section = this.dimensionHelpers[109](componentData, context);
    const family = this.dimensionHelpers[111](componentData, context);
    const subfamily = this.dimensionHelpers[113](componentData, context);
    return `${section}/${family}/${subfamily}`;
  }

  public static getCategoryPath(categoryId?: string, context?: EcommerceContext): string | undefined {
    let parsedPath;
    if (categoryId && context?.categories) {
      const flatCategories = CategoriesHelpers.flatCategoriesMap(context.categories);
      const categoriesPath = flatCategories[categoryId]?.sectionPathEn;
      parsedPath = categoriesPath ? AnalyticsHelpersOysho.parsePath(categoriesPath) : undefined;
    }
    return parsedPath;
  }

  public static formatEcommerceProductPrice(price: string): string {
    return price.replace(',', '.').substring(0, price.length - 2);
  }

  public static formatProducts(products: AnalyticsComponentProduct[]): AnalyticsComponentProduct[] {
    const formattedProducts: AnalyticsComponentProduct[] = [];
    products.forEach((product) => {
      const gridElemType = product.gridElemType.toLowerCase();
      if (gridElemType === 'bundle' || gridElemType === 'lookbook') {
        product.bundleProducts.forEach((bundleProduct) => {
          formattedProducts.push({ ...product, ...bundleProduct });
        });
      } else {
        formattedProducts.push({ ...product });
      }
    });
    return formattedProducts;
  }

}
